/*
 * The /ai-blogging-platform deck: an AI-first blog platform. Publish from Claude or
 * ChatGPT through MCP, or write in the editor. Review a
 * post and choose its destinations before publishing or sending.
 *
 * Dashboard review is the default story everywhere on this deck. The FAQ
 * qualifies confirmation behavior by connection instead of promising one
 * approval or automation model everywhere.
 *
 * What moved off this deck rather than being deleted: the "What's included"
 * list is `PLATFORM_PAGE.included` (/pricing, with the transactional email
 * line), and the assembled-stack table is `PRICING_PAGE.stack` (/pricing).
 */
export const LANDING = {
  hero: {
    /*
     * One headline, two fields: the Hero renders the second half in the
     * accent colour, and a nested span cannot live in a content string.
     */
    headline: "Your blog. Your voice.",
    headlineAccent: "Publish everywhere.",
    subhead:
      "Write, preview and publish from Claude or ChatGPT through GoodSender’s official MCP server, or write in the editor. Your blog, newsletter, and audience in one place. Review each post and choose its destinations before you publish or send.",
    toolsCta: "Explore AI tools",
    /*
     * The email-expertise claim, kept in the hero (operator, 2026-10-07): the
     * publishing workflow leads, but being email people is the part
     * blog tools cannot catch up with. The visual publishing package carries
     * the feature details, leaving this line to establish the pedigree.
     *
     * "Most" bounds the contrast (PR #907 review's rule: some tools in the
     * category do run their own sending). "Rent their email" is the
     * owns-rather-than-rents framing already on record in llms-prod.txt and
     * the `deliverability` FAQ entry. Deliberately not the footer's sentence
     * ("Built by..."): content.test.ts holds the pedigree surfaces to
     * distinct sentences.
     */
    proofLine:
      "Made by email people, the team behind SendGrid and Laneful. Most blog tools rent their email. We built ours.",
    /*
     * The hero visual: a fictional personal travel blog, from a finished
     * draft to approval. Paris makes the example familiar beyond a technical
     * audience. The reserved .example domain keeps it clearly illustrative.
     * The complete static package is visible on the server paint.
     */
    session: {
      chrome: "Emma’s travel journal",
      draft: "Draft ready · 5 min read",
      preview: "Preview: emma.example/weekend-in-paris",
      destinationsLabel: "Publish to",
      destinations: ["Blog", "LinkedIn", "X", "Newsletter"],
      approve: "Approve and publish",
    },
    /*
     * The accessible name for the session figure: one labelled image rather
     * than fragments of schematic chrome (#792 review's rule).
     */
    sessionLabel:
      "An illustrative personal blog by Emma: a draft called A weekend in Paris, an Eiffel Tower cover, a preview link, selected destinations (blog, LinkedIn, X, newsletter), and an Approve and publish button.",
    sessionCaption: "Your writing. Your channels. Your final say.",
  },

  design: {
    eyebrow: "AI blog design",
    title: "Your taste. Your blog. Just ask.",
    body: "Describe the look you want. Refine it with AI. Preview your blog before you save it, on your own domain.",
    choicesLabel: "Explore example design directions",
    promptLabel: "You describe it",
    previewLabel: "Illustrative blog design preview",
    caption: "Illustrative designs. Your direction, made with AI.",
    features: ["Your own domain", "Your brand", "Preview before saving"],
    choices: [
      {
        id: "editorial",
        name: "Editorial",
        prompt: "Warm paper, oversized serif headlines, a little burnt orange.",
        brand: "FORM & FIELD",
        category: "Ideas for a more intentional internet",
        title: "Good things take a point of view.",
        issue: "THE DESIGN ISSUE",
        article: "A quieter kind of progress",
        secondary: "Build something worth returning to.",
      },
      {
        id: "studio",
        name: "Studio",
        prompt: "Make it bold. Dark ink, electric lime, a design studio feel.",
        brand: "OFFSCRIPT®",
        category: "Independent thinking. Shared out loud.",
        title: "Less expected. More you.",
        issue: "NOTES FROM THE STUDIO",
        article: "Make room for the unexpected",
        secondary: "Small experiments. Big possibilities.",
      },
      {
        id: "journal",
        name: "Journal",
        prompt:
          "A thoughtful journal. Forest green, clean type, plenty of space.",
        brand: "EVERGREEN",
        category: "Fresh perspectives for the long run",
        title: "Ideas worth growing.",
        issue: "THE NEXT CHAPTER",
        article: "The beauty of starting small",
        secondary: "A field guide to what comes next.",
      },
    ],
  },

  visual: {
    heroEyebrow: "Your blog. Your audience. Your AI.",
    ready: "Ready for your review",
    draftTitle: "A weekend in Paris",
    draftCategory: "TRAVEL / PARIS",
    draftSummary: "My favorite cafés, long walks, and little discoveries.",
    packageParts: ["Title & tags", "Cover image", "Channel copy"],
    replay: "Replay flow",
    flowLabel:
      "Publishing workflow: write in the editor or with your own tools, review your post, then publish to the destinations you choose.",
    draftExcerpt: "I started the day with a coffee and no plans.",
    reviewItems: [
      "Post & cover",
      "Preview & title options",
      "Social & newsletter copy",
    ],
    approval: "You approve",
    published: "Selected destinations live.",
    status: "Status for every destination",
    hub: "One approved post",
    hubNote: "You pick the channels",
    draftSources: ["Editor", "MCP", "CLI", "Skills"],
  },

  steps: {
    eyebrow: "How it works",
    title: "Write. Review. Publish.",
    items: [
      {
        num: "01",
        name: "Write your way",
        body: "Start in the editor, or use MCP, CLI, and Skills to bring your own AI into the workflow.",
      },
      {
        num: "02",
        name: "Review your post",
        body: "Check your writing, cover, and preview. Choose the copy and destinations for sharing.",
      },
      {
        num: "03",
        name: "Publish once",
        body: "Publish your post, then send it only to the destinations you selected. You get a status for each one.",
      },
    ],
    caption: "your words → your review → your readers",
  },

  tools: {
    eyebrow: "MCP · CLI · Skills",
    title: "Bring your AI. Keep your voice.",
    lede: "Connect the assistant you already use. Give it a workflow. Stay in charge of what goes live.",
    direction: "Your direction",
    assistant: "Your AI assistant",
    prompt: "Check my Paris post and prepare sharing copy. Keep my voice.",
    inputs: ["Your words", "Your photos", "Your ideas"],
    connection: "The connection",
    connectionName: "MCP or CLI",
    connectionBody: "Lets your assistant work with your GoodSender blog.",
    skill: "The workflow",
    skillName: "Skills",
    skillFile: "goodsender-blog / SKILL.md",
    skillBody:
      "Guides your assistant through your voice, fact checks, and sharing copy.",
    review: "Your final say",
    workspace: "Ready in GoodSender",
    checks: ["Post & preview", "Titles & tags", "Sharing copy"],
    approval: "You review. You approve.",
    note: "In the dashboard, review your post and choose its destinations before you publish or send.",
    setupCta: "Connect your assistant",
    articleCta: "See how the skill works",
    articleHref:
      "https://blog.goodsender.com/skill-makes-blogging-with-ai-easier",
  },

  /*
   * Every destination named here ships today: the blog at a GoodSender-provided
   * address or on the customer's domain, and the syndication integrations
   * released in every environment (#1044;
   * `src/components/integrations/availability.ts`).
   */
  destinations: {
    eyebrow: "Where it goes",
    title: "Choose each destination",
    items: [
      {
        id: "blog",
        name: "Your blog",
        body: "At a GoodSender-provided address or on your own domain.",
      },
      {
        id: "social",
        name: "X, LinkedIn, Bluesky, Mastodon",
        body: "Announcement copy written for each platform.",
      },
      {
        id: "syndication",
        name: "dev.to",
        body: "The full article, with a canonical URL back to your blog.",
      },
      {
        id: "newsletter",
        name: "Newsletter",
        body: "Its own subject line, summary, and link, sent to confirmed subscribers when selected.",
      },
    ],
    note: "In the dashboard, review your post, then choose the destinations to publish or send.",
  },

  /*
   * Deliverability, compressed to three cards (2026-10-06): proof that the
   * newsletter lands, not the pitch. "Deliverability" stays the eyebrow so the
   * word a reader scans for is a heading; the `deliverability` FAQ entry is
   * where the long form lives.
   */
  proof: {
    eyebrow: "Deliverability",
    title: "Why it reaches the inbox",
    points: [
      {
        name: "Permission, enforced.",
        body: "Double opt-in is the default.",
      },
      {
        name: "Engagement, monitored.",
        body: "Quiet subscribers get re-confirmed; complaints and bounces are suppressed.",
      },
      {
        name: "Reputation, earned.",
        body: "Every send runs on Laneful, from the team that built SendGrid.",
      },
    ],
  },

  /*
   * The docs 03 condensed pricing teaser (D-4 in the spec): the bands and the
   * two worked examples are rendered by `PricingTeaser.tsx` straight from
   * `PRICING_PAGE.bands` / `PRICING_PAGE.examples`, so no figure lives here
   * and the one-source rule that covers every other number covers these.
   */
  pricingTeaser: {
    eyebrow: "Pricing",
    title: "Pay for sends, not subscribers",
    /*
     * "in flat bands" is back (PR #907 review): the bands are the mechanism
     * behind "pay for sends, not subscribers", `PRICING_PAGE.bands` renders
     * them right below, and the `ghost` FAQ answer quotes a band price.
     * Dropping it left the title asserting a model the copy no longer named.
     */
    lede: "Grow your audience without growing your bill. Pay for sends in flat bands. Most businesses never leave the free tier.",
    cta: "Run your numbers",
  },

  who: {
    forTitle: "Built for",
    forItems: [
      "Independent writers and experts building a home for their ideas.",
      "Small teams sharing a point of view, product updates, or a company story.",
      "B2B SaaS and dev tool teams running a content blog and a product newsletter.",
    ],
    forAside:
      "Anyone building a blog and a permission-based newsletter without a stitched-together stack.",
    notTitle: "Not for",
    notItems: [
      "Anyone shopping for paid subscriptions, an ad network, recommendations, or comments.",
      "Teams that need a bulk post importer.",
      "Enterprise marketing automation suites.",
      "Cold outreach. Still spam. Still a hard no.",
    ],
    closing:
      "Content marketing, not marketing automation. Owned audiences, not purchased lists.",
  },

  finalCta: {
    headline: "Your words. Your blog. Your audience.",
    // Keep this closing pitch distinct from the AI tools page.
    body: "Write, publish, and reach your readers from one platform. Free to start, priced like infrastructure as you grow.",
    closing: "No paywalls. No ad network. A direct line to your readers.",
  },
} as const;
